// React Query hooks for the nostro-operations feature.
// Ported from src/app/services/nostro-operations.service.ts.
// PaymentIntermediary listing now comes from the payment-core endpoint via
// `@/features/payment-intermediaries/api`.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { appConfig } from '@/api/appConfig';
import type { M3ApiResponse } from '@/types/api';
import type {
  AirtableFindNostroOperationsRecordsRequest,
  ConfirmNostroOperationRequest,
  ExternalServicesOperationsRecord,
  NostroAccount,
  NostroAccountAdjustRequest,
  StatsAddWithdrawFundsResponse,
} from '@/types/nostro';

// Re-export the canonical payment-intermediaries query.
export { usePaymentIntermediariesQuery } from '@/features/payment-intermediaries/api';

interface AirtableRecordsResult {
  records: ExternalServicesOperationsRecord[];
}

const NOSTRO_RECORDS_KEY = ['nostro-operations', 'records'] as const;
const NOSTRO_STATS_KEY = ['nostro-operations', 'stats'] as const;

export function useNostroOperationsQuery(
  params: AirtableFindNostroOperationsRecordsRequest,
) {
  return useQuery({
    queryKey: [...NOSTRO_RECORDS_KEY, params],
    queryFn: async () => {
      const { data } = await apiClient.post<M3ApiResponse<AirtableRecordsResult>>(
        `${appConfig.airtableApi}/getNostroOperationsRecords`,
        params,
      );
      return data.result.records;
    },
  });
}

export function useNostroStatsQuery(status: string) {
  return useQuery({
    queryKey: [...NOSTRO_STATS_KEY, status],
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<StatsAddWithdrawFundsResponse>>(
        `${appConfig.airtableApi}/statsNostroOperationsFunds/${status}`,
      );
      return data.result;
    },
  });
}

export function useConfirmNostroOperationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await apiClient.post<M3ApiResponse<AirtableRecordsResult>>(
        `${appConfig.airtableApi}/confirmNostroOperation`,
        formData,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NOSTRO_RECORDS_KEY });
      qc.invalidateQueries({ queryKey: NOSTRO_STATS_KEY });
    },
  });
}

export function useCancelNostroOperationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: ConfirmNostroOperationRequest) => {
      const { data } = await apiClient.post<M3ApiResponse<AirtableRecordsResult>>(
        `${appConfig.airtableApi}/cancelNostroOperation`,
        request,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NOSTRO_RECORDS_KEY });
      qc.invalidateQueries({ queryKey: NOSTRO_STATS_KEY });
    },
  });
}

// ---- Nostro account management (admin API) --------------------------------

export function useNostroAccountsQuery() {
  return useQuery({
    queryKey: ['nostro-accounts'],
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<NostroAccount[]>>(
        `${appConfig.adminApi}/nostroAccounts`,
      );
      return data.result;
    },
  });
}

export function useNostroAccountAdjustMutation() {
  return useMutation({
    mutationFn: async (request: NostroAccountAdjustRequest) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.adminApi}/nostroAccountAdjust`,
        request,
      );
      return data;
    },
  });
}

export function useNostroAccountAddFundsMutation() {
  return useMutation({
    mutationFn: async (request: NostroAccountAdjustRequest) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.adminApi}/nostroAccountAddFunds`,
        request,
      );
      return data;
    },
  });
}

export function useNostroAccountWithdrawFundsMutation() {
  return useMutation({
    mutationFn: async (request: NostroAccountAdjustRequest) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.adminApi}/nostroAccountWithdrawFunds`,
        request,
      );
      return data;
    },
  });
}
